import { modules } from "../core/platform/registry/registry";
import { createPeripheralsModule } from "./peripherals";
import { createInventoryModule } from "./inventory";
import { createBooksModule } from "./books";
import { createLoyaltyModule } from "./loyalty";

/**
 * Module bootstrap — the one place a module is loaded into the registry.
 *
 * THIS FILE IS WHY `enabled = 1` IN `module_registration` MEANS ANYTHING. Until
 * it existed the flag travelled from D1 to `/api/config` to the till and was
 * stamped onto each finalized sale as provenance, and stopped there: nothing
 * ever called `modules.register`, so the registry was empty, every `<Slot>`
 * rendered nothing, and the drawer's event subscribers were never attached. The
 * module was complete, tested, deployed — and unreachable.
 *
 * IT LIVES IN `src/modules/`, NOT IN CORE. `src/core/**` must not import
 * `src/modules/**` (Principle III, enforced by `npm run check:boundaries`), so
 * the composition of "which modules exist" belongs on this side of the
 * boundary. The shell imports this; core still knows nothing about peripherals.
 */

/**
 * Registration is separate from enabling and happens exactly once.
 *
 * `register()` throws on a duplicate id, and this runs from a React effect —
 * which StrictMode invokes twice in development — so the guard is load-bearing
 * rather than defensive.
 */
let registered = false;

function registerAll(): void {
  if (registered) return;
  modules.register(createPeripheralsModule());
  modules.register(createInventoryModule());
  modules.register(createBooksModule());
  modules.register(createLoyaltyModule());
  registered = true;
}

/**
 * Bring the registry in line with what this deployment has switched on.
 *
 * Called with the list from `/api/config`, which the terminal caches, so a
 * drawer bought on Tuesday is live on Tuesday without a deploy — and one that
 * breaks on Wednesday morning is switched off from the back office rather than
 * by pushing code while a queue waits.
 *
 * A MODULE THAT FAILS TO START MUST NOT TAKE THE TILL WITH IT. `onEnable` talks
 * to hardware — it probes WebUSB and opens a device — which is exactly the kind
 * of thing that throws on a terminal where someone unplugged a printer over the
 * weekend. Selling is core and must not depend on any of it, so a failure is
 * logged and the till carries on without that module, the same isolation
 * `SlotBoundary` gives a module's UI.
 */
export async function syncEnabledModules(enabled: readonly string[]): Promise<void> {
  registerAll();

  const wanted = new Set(enabled);

  for (const definition of modules.all()) {
    const shouldRun = wanted.has(definition.id);
    if (shouldRun === modules.isEnabled(definition.id)) continue;

    try {
      if (shouldRun) await modules.enable(definition.id);
      else await modules.disable(definition.id);
    } catch (error) {
      console.error(
        `[modules] "${definition.id}" failed to ${shouldRun ? "enable" : "disable"}`,
        error,
      );
    }
  }
}
